import FileViewerByFileUrl from "components/FileViewerByFileUrl";
import MainCard from "components/MainCard";
import { uniqueByEquipmentPartIdAndLogCode } from "data/data";
import useConfig from "hooks/useConfig";
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next";
import AirStatus from "./AirStatus";
import EquipmentDurationGraphs from "../chart/EquipmentDurationGraphs";
import DiagnosisCard from "components/cards/statusTab/DiagnosisCard";
import MainOperationCard from "components/cards/statusTab/MainOperationCard";
import OpModeCard from "components/cards/statusTab/OpModeCard";
import SubOperationCard from "components/cards/statusTab/SubOperationCard";
import SubStatusInfoCard from "components/cards/statusTab/SubStatusInfoCard";
import DiagnosisPartCard from "components/cards/statusTab/DiagnosisPartCard";
import OperationCard from "components/cards/statusTab/OperationCard";
import { getAhuIaqLogsLatestByEquipmentId } from "components/axios/ahuIaqLogAxios";
import OpInfoCard from "components/cards/statusTab/OpInfoCard";
import ModelViewerCard from "./ModelViewerCard";

const IAQ_DELAY = 10 * 1000; // 10초

const MESSAGE_TYPE_PRIORITY = {
    RISK: 3,
    ACTION: 2,
    INFO: 1,
};

function getLocalizedMessageText(message, lang) {
    if (!message) return '';

    // 정확히 일치하는 언어 (ko, en 등)
    if (lang && message[lang]) {
        return message[lang];
    }

    // lang이 ko-KR / en-US 같은 경우 대비
    if (lang && lang.includes('-')) {
        const shortLang = lang.split('-')[0];
        if (message[shortLang]) {
            return message[shortLang];
        }
    }

    // fallback
    return message.text || '';
}

function resolveAhuMessages(messages = []) {
    if (!Array.isArray(messages) || messages.length === 0) {
        return {
            primary: null,
            secondary: [],
        };
    }

    const filteredMessages = messages.filter(msg => msg.domain === 'CONTROL');
    if (filteredMessages.length === 0) {
        return {
            primary: null,
            secondary: [],
        };
    }

    // 1️⃣ 정렬
    const sorted = [...filteredMessages].sort((a, b) => {
        // type 우선순위
        const typeDiff =
            (MESSAGE_TYPE_PRIORITY[b.type] || 0) -
            (MESSAGE_TYPE_PRIORITY[a.type] || 0);

        if (typeDiff !== 0) return typeDiff;

        // severity 내림차순
        return (b.severity || 0) - (a.severity || 0);
    });

    return {
        primary: sorted[0],
        secondary: sorted.slice(1),
    };
}

const StatusTab = ({info, measurementLogsLatest, equipmentPartsExistSeneorPosition, imgName, equipmentParts, equipmentPartsByComponentId, setLocationCode, activeVideoPartId}) => {
    const platformServer = process.env.REACT_APP_PLATFORM_SERVER;
    const { t, i18n } = useTranslation();
    const { mode } = useConfig();
// console.log(measurementLogsLatest)
    const tabMaxCount = 8;

    const [aiIaq, setAiIaq] = React.useState(null);

    const alertCnt = info?.status === 'O3_A' ? 1 : 0;
    const referCnt = info?.status === 'O2_I' ? 1 : 0;

    const showAlert = alertCnt > 0 || referCnt > 0;
    const alertType = alertCnt > 0 ? "alert" : referCnt > 0 ? "refer" : "";

    const logCodes = useMemo(() => {
        return (process.env.REACT_APP_TEMPERATURE_VIEW_YN === 'Y')
        ? ['T', 'RH', 'CO2']
        : ['RH', 'CO2'];
    });

    const derived = useMemo(() => {
        if (!measurementLogsLatest || !info) {
            return {
                ventilation: [],
                airSupply: [],
                outdoorAir: [],
                emission: [],
                subOperationInfos: [],
                subStatusInfos: [],
                equipmentPartDiagnosis: [],
                opModeName: null,
                isOperationInfo: false,
                isStatusInfo: false,
            };
        }

        let tabCount = 2;
// console.log(info.latestEquipmentOp?.opMode)
        // 운행모드
        let opModeName = null;
        if (info.latestEquipmentOp?.opMode?.integratedName === 'Cooling') {
            opModeName = t('message.common.text.opmode.cooling');
            tabCount++;
        } else if (info.latestEquipmentOp?.opMode?.integratedName === 'Heating') {
            opModeName = t('message.common.text.opmode.heating');
            tabCount++;
        } else if ((info.equipmentCtgr.parent.parent.code === 'EV' || info.equipmentCtgr.parent.parent.code === 'ES'
                    || info.equipmentCtgr.code.startsWith('DOUBLE.') || info.equipmentCtgr.code.startsWith('SINGLE.')
                    || info.equipmentCtgr.code.startsWith('GEARED.') || info.equipmentCtgr.code.startsWith('GEARLESS.'))
                        && info.latestEquipmentOp?.opMode?.integratedName) {
            opModeName = info.latestEquipmentOp?.opMode?.integratedName;
            tabCount++;
        }
        // console.log(opModeName)
        const controlUnitsWithIAQ = measurementLogsLatest.filter(
            m => m.componentCode === 'controlUnit' && m.equipmentPartCode === 'IAQSensor'
        );

        const ventilation = controlUnitsWithIAQ.filter(
            m => m.componentLocationCode === 'RA' && logCodes.includes(m.logCode)
        );

        const airSupply = controlUnitsWithIAQ.filter(
            m => m.componentLocationCode === 'SA' && logCodes.includes(m.logCode)
        );

        const outdoorAir = controlUnitsWithIAQ.filter(
            m => m.componentLocationCode === 'OA' && logCodes.includes(m.logCode)
        );

        const emission = measurementLogsLatest.filter(
            m =>
            m.componentCode === 'DamperUnit' &&
            m.equipmentPartCode === 'DAMPER' &&
            m.logCode === 'POS' &&
            ['EA', 'OA', 'OA2'].includes(m.componentLocationCode)
        );

        const controlUnits2 = measurementLogsLatest.filter(
            m =>
            m.componentCode === 'controlUnit' &&
            m.equipmentPartCode !== 'IAQSensor' &&
            !['ENA', 'OUTPUT'].includes(m.logCode) &&
            (process.env.REACT_APP_TEMPERATURE_VIEW_YN === 'Y' || (process.env.REACT_APP_TEMPERATURE_VIEW_YN === 'N' && !['T', 'T_ST', 'T_SPT'].includes(m.logCode))) &&
            (m.logCode !== 'AL' || m.logValue === '1')
        );
        // console.log(controlUnits2)
        const subOperationInfos = uniqueByEquipmentPartIdAndLogCode(
            controlUnits2,
            tabMaxCount - tabCount
        );
        tabCount += subOperationInfos.length;

        let isOperationInfo = false;
        let isStatusInfo = false;

        // if (
        //     tabCount < tabMaxCount &&
        //     info.latestEquipmentOp?.opStatus === 'ACTIVE' &&
        //     (info.latestEquipmentOp?.opStatusDetail || info.latestEquipmentOp?.doorStatus)
        // ) {
        //     isOperationInfo = true;
        //     tabCount++;
        // }

        if (tabCount < tabMaxCount) {
            isStatusInfo = true;
        }

        const subStatusInfos = isStatusInfo
            ? uniqueByEquipmentPartIdAndLogCode(
                controlUnitsWithIAQ.filter(
                m =>
                    m.componentLocationCode === 'RA' &&
                    logCodes.includes(m.logCode)
                ),
                tabMaxCount - tabCount
            )
            : [];

        tabCount += subStatusInfos.length;

        const equipmentPartDiagnosis =
            tabCount < tabMaxCount && equipmentPartsExistSeneorPosition
            ? equipmentPartsExistSeneorPosition.slice(0, tabMaxCount - tabCount)
            : [];

        tabCount += equipmentPartDiagnosis.length;

        return {
            ventilation,
            airSupply,
            outdoorAir,
            emission,
            subOperationInfos,
            subStatusInfos,
            equipmentPartDiagnosis,
            opModeName,
            isOperationInfo,
            isStatusInfo,
            // tabCount,
        };
    }, [measurementLogsLatest, info, equipmentPartsExistSeneorPosition, t, tabMaxCount]);

    const {
        ventilation,
        airSupply,
        outdoorAir,
        emission,
        subOperationInfos,
        subStatusInfos,
        equipmentPartDiagnosis,
        opModeName,
        isOperationInfo,
        isStatusInfo,
        // tabCount,
    } = derived;

    const cards = useMemo(() => {
        if (!info) return [];

        const list = [];

        // 1️⃣ 진단정보 (info?.status)
        list.push({
            key: 'diagnosis',
            element: <DiagnosisCard info={info} />
        });

        // 2️⃣ 메인 운전정보 (info.latestEquipmentOp?.opStatus)
        if (info?.latestEquipmentOp?.opStatus) {
            list.push({
                key: 'main-operation',
                element: <MainOperationCard info={info} />
            });
        }

        // 3️⃣ 운행모드 (info.latestEquipmentOp?.opMode)
        if (opModeName) {
            list.push({
                key: 'opmode',
                element: <OpModeCard opModeName={opModeName} /> 
            });
        }
        
        // 현재층 info.equipmentCtgr.parent.parent.code === 'EV'            info.latestEquipmentOp?.currentFloor
        if (info.equipmentCtgr.parent.parent.code === 'EV' && info.latestEquipmentOp?.currentFloor) {
            list.push({
                key: 'opmode-currentFloor',
                element: <OpInfoCard type="currentFloor" title={t('message.common.subtitle.currentFloor')} opModeName={info.latestEquipmentOp?.currentFloor} /> 
            });
        }
        // 운행방향  info.latestEquipmentOp?.opStatus 'ACTIVE'              info.latestEquipmentOp?.opStatusDetail
        if ((info.equipmentCtgr.parent.parent.code === 'EV' || info.equipmentCtgr.parent.parent.code === 'ES' 
                || info.equipmentCtgr.code.startsWith('DOUBLE.') || info.equipmentCtgr.code.startsWith('SINGLE.')
                || info.equipmentCtgr.code.startsWith('GEARED.') || info.equipmentCtgr.code.startsWith('GEARLESS.')
                || info.equipmentCtgr.code.startsWith('DBERAIL.')) 
            && info.latestEquipmentOp?.opStatus == 'ACTIVE' && (info.latestEquipmentOp?.opStatusDetail || info.latestEquipmentOp?.doorStatus)) {
        // if (info.latestEquipmentOp?.opStatus == 'ACTIVE' && (info.latestEquipmentOp?.opStatusDetail || info.latestEquipmentOp?.doorStatus)) {
            list.push({
                key: 'opmode-direction',
                element: <OpInfoCard type="direction" title={t('message.common.subtitle.direction')} opModeName={info.latestEquipmentOp?.opStatusDetail ||
                        info.latestEquipmentOp?.doorStatus || '-'} /> 
            });
        }

        // 오류 info?.latestEquipmentOp?.controlPanelCode?.alertYn == 'Y'   info?.latestEquipmentOp?.controlPanelCode?.usrDfnCd
        if ((info.equipmentCtgr.parent.parent.code === 'EV' || info.equipmentCtgr.parent.parent.code === 'ES'
            || info.equipmentCtgr.code.startsWith('DOUBLE.') || info.equipmentCtgr.code.startsWith('SINGLE.')
            || info.equipmentCtgr.code.startsWith('GEARED.') || info.equipmentCtgr.code.startsWith('GEARLESS.')) 
            && info?.latestEquipmentOp?.controlPanelCode?.alertYn == 'Y' && info.latestEquipmentOp?.controlPanelCode?.usrDfnCd) {
            list.push({
                key: 'opmode-operationError',
                element: <OpInfoCard type="operationError" title={t('message.common.subtitle.operationError')} /*opModeName={'Error'} */opModeName={info?.latestEquipmentOp?.controlPanelCode?.usrDfnCd} /> 
            });
        }

        // 총 가동시간
        
// console.log(subOperationInfos)
        // 4️⃣ 서브 가동 정보 (data.logName data.logValue)
        subOperationInfos.forEach((opInfo, idx) => {
            list.push({
                key: `sub-op-${idx}`,
                element: <SubOperationCard data={opInfo} />
            });
        });

        // // 5️⃣ 운전 상태 정보 (info.latestEquipmentOp?.opStatusDetail || info.latestEquipmentOp?.doorStatus)
        // if (isOperationInfo && info.latestEquipmentOp?.opStatus && info.latestEquipmentOp?.opStatus === 'ACTIVE') {
        //     list.push({
        //         key: 'operation-info',
        //         element: <OperationCard info={info} /> 
        //     });
        // }
// console.log(subStatusInfos)
        // 6️⃣ 상태 정보 (data.logValue data.logUnit or graph)
        if (isStatusInfo) {
            subStatusInfos.forEach((stInfo, idx) => {
                list.push({
                    key: `status-${idx}`,
                    element: <SubStatusInfoCard data={stInfo} />
                });
            });
        }

        // 7️⃣ 진단 부품 (data.lowestDiagnosisRank)
        equipmentPartDiagnosis.forEach((part, idx) => {
            list.push({
                key: `diagnosis-${idx}`,
                element: <DiagnosisPartCard data={part} />
            });
        });

        return list.slice(0, tabMaxCount); // ⭐ 핵심
    }, [
        info,
        opModeName,
        subOperationInfos,
        subStatusInfos,
        equipmentPartDiagnosis,
    ]);

    // useEffect(() => {
    //     console.log('equipmentParts 변경:', equipmentParts);
    // }, [equipmentParts]);

    useEffect(() => {
        if (!info?.id) return;

        let timer;

        const fetchIaq = async () => {
            try {
                const res = await getAhuIaqLogsLatestByEquipmentId(info.id);
                // console.log(res)
                // 서버 구조에 맞게 조정
                setAiIaq(res || null);
            } catch (e) {
                // console.error("IAQ fetch error", e);
            }
        };

        // 최초 1회
        fetchIaq();

        // ⏱ 실시간 반영 (예: 10초)
        timer = setInterval(fetchIaq, IAQ_DELAY);

        return () => clearInterval(timer);
    }, [info?.id]);

    const { primary: aiPrimaryMsg, secondary: aiSecondaryMsgs } = useMemo(() => {
        return resolveAhuMessages(aiIaq?.messages);
    }, [aiIaq?.messages]);

    return (
        <React.Fragment>
            <div className='equipment_inbox1 equipment_inbox11'>
                {cards.map(card => (
                <React.Fragment key={card.key}>
                    {card.element}
                </React.Fragment>
                ))}

                {cards.length < tabMaxCount &&
                Array.from({ length: tabMaxCount - cards.length }).map((_, i) => (
                <MainCard key={`empty-${i}`} content={false} className="squarebox empty_box">
                    <div className='alert_phrase' />
                </MainCard>
                ))}
            </div>

            <div className='equip_alert_view'>
              <div className='elevator_motion equip_view_message equip_view_nomessage'>
                {aiIaq &&
                <div className='equip_ai'>
                    <div className='ai_part1'>
                        <p>{t('message.common.subtitle.air.quality.index')}</p>
                        <div className="air_score">{aiIaq.iaq?.displayIndex?.toFixed(0) ?? '-'}</div>
                        {/* <div className="air_score">85</div> */}
                    </div>
                    <div className='ai_part2'>
                        <p>{t('message.common.subtitle.AI.recommendations')}</p>
                        <div>
                            {/* 외기개도 : 13.1%<br/>
                            외기2개도 : 0.0%<br/>
                            배기개도 : 9.0%<br/>
                            혼합개도 : 92.2%<br/>
                            DVM(OA) : 정지<br/> */}
                            {t('message.common.text.oa')} : {aiIaq.raw?.oa1?.toFixed(1) ?? '-'}%<br/>
                            {t('message.common.text.oa2')} : {aiIaq.raw?.oa2?.toFixed(1) ?? '-'}%<br/>
                            {t('message.common.text.ea')} : {aiIaq.raw?.ea?.toFixed(1) ?? '-'}%<br/>
                            {t('message.common.text.mix')} : {aiIaq.raw?.mix?.toFixed(1) ?? '-'}%<br/>
                            DVM(OA) : {aiIaq.raw?.dvmOaEnable ? `${aiIaq.raw?.dvmOaTemp ?? '-'}℃`:t('message.common.text.stop')}<br/>
                            {/* DVM(OA) : {aiIaq.pv?.dvmOaEnable ? t('message.common.text.active'):t('message.common.text.stop')}<br/> */}
                            {/* {aiIaq.pv?.dvmOaEnable &&
                            <>DVM(OA) : {aiIaq.pv?.dvmOaTemp ?? '-'}℃<br/></>
                            } */}
                        </div>
                    </div>
                    <div className='ai_part3'>
                        <p>{t('message.common.subtitle.AI.opinion')}</p>
                        <div>
                            {/* 외기 미세먼지로 환기 확대를 제한합니다.<br/>
                            최소환기 수준으로만 OA1을 유지하고 OA2(열보정)는 사용하지 않는 방향이 안전/합리적입니다. */}
                            {aiPrimaryMsg ? (
                                <div className={`ai_msg ${aiPrimaryMsg.type.toLowerCase()}`}>
                                    <p>{getLocalizedMessageText(aiPrimaryMsg.message, i18n.language)}</p>
                                </div>
                            ) : (
                                <div>-</div>
                            )}

                            {/* 필요 시 참고 메시지 */}
                            {aiSecondaryMsgs.length > 0 && (
                                <div className="ai_msg_sub">
                                    {aiSecondaryMsgs.map((msg, idx) => (
                                    <p key={idx}>
                                        {getLocalizedMessageText(msg.message, i18n.language)}
                                    </p>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                }
                <div className={`equip_view_img${imgName.toLowerCase().endsWith('.mp4') ? '' : ' gif'}`}
                    style={{background : `${imgName.toLowerCase().endsWith('.mp4') ? 'unset' : (info.latestEquipmentOp?.opStatus || 'stop').toUpperCase() === 'ACTIVE' ? "url('/img/view/bg_ani.gif') no-repeat center top" : "url('/img/view/bg_stop.gif') no-repeat center top"}`/*, backgroundSize : "cover"*/}}
                    >
                    {(info.latestEquipmentOp?.opStatus || 'stop').toUpperCase() === 'ACTIVE' &&
                    <React.Fragment>
                    <AirStatus
                        data={outdoorAir}
                        emission={emission}
                        showLogs={logCodes}
                        emissionCodes={['OA', 'OA2']}
                        className='view_img_pop1'
                    />
                    <AirStatus
                        emission={emission}
                        emissionCodes={['EA']}
                        className='view_img_pop2'
                    />
                    <AirStatus
                        data={ventilation}
                        showLogs={logCodes}
                        className='view_img_pop3'
                    />
                    <AirStatus
                        data={airSupply}
                        showLogs={logCodes}
                        className='view_img_pop4'
                    />
                    </React.Fragment>
                    }

                    {/* <div className="alert_box">
                        {alertCnt > 0 ? <div className="orange_c" style={{zIndex:"2"}}><span>!</span></div> :
                        (
                        (referCnt > 0 ? <div className="yellow_c" style={{zIndex:"2"}}><span>!</span></div> : '')
                        )}
                    </div> */}

                    {/* {info.equipmentCtgr.parent.parent.code === 'EV' && info.latestEquipmentOp && <p className='floor'><span className='font_green'>{info.latestEquipmentOp?.opMode?.integratedName || ''}</span><span>{info.latestEquipmentOp?.currentFloor || '-'}</span><span className={`${info.latestEquipmentOp?.opStatus === 'DOWN' ? 'downarrow':''} font_blue`}>{info.latestEquipmentOp?.opStatus === 'UP' ?'▲':info.latestEquipmentOp?.opStatus === 'DOWN' ? '▼':''}</span></p>}
                    {info.equipmentCtgr.parent.parent.code === 'ES' && info.latestEquipmentOp && <p className='floor'><span className='font_green'>{info.latestEquipmentOp?.opMode?.integratedName || ''}</span><span>-</span><span className={`${info.latestEquipmentOp?.opStatus === 'DOWN' ? 'downarrow':''} font_blue`}>{info.latestEquipmentOp?.opStatus === 'UP' ?'▲':info.latestEquipmentOp?.opStatus === 'DOWN' ? '▼':''}</span></p>} */}
                    {}
                    {/* {platformServer === 'NANTONG' && imgName && 
                        <video src={`${process.env.PUBLIC_URL}/movie/${imgName}`}
                        className="detailEQMovie" preload='auto' autoPlay loop muted
                        ></video>}
                    {platformServer !== 'NANTONG' && imgName && (imgName.toLowerCase().endsWith('.mp4') ? 
                        <video src={`${process.env.PUBLIC_URL}/movie/${imgName}`} className="detailEQMovie" preload='auto' autoPlay loop muted></video>
                        : <img src={`${process.env.PUBLIC_URL}/img/view/${imgName}`} alt='' />)} */}

                    {/* {(info.equipmentCtgr.parent.parent.code === 'EV' || info.equipmentCtgr.parent.parent.code === 'ES') && info.latestEquipmentOp &&
                        <p className={`error${info?.latestEquipmentOp?.controlPanelCode?.alertYn === 'N' ? ' font_blue': ''}`}>
                        {info?.latestEquipmentOp?.controlPanelCode?.alertYn === 'N' ? t('message.common.normal') : info?.latestEquipmentOp?.controlPanelCode?.usrDfnCd}
                        </p>
                    } */}
                    {imgName && (imgName?.toLowerCase().endsWith('.mp4') ? 
                        <>
                            <div className="alert_box">
                                {alertCnt > 0 ? <div className="orange_c" style={{zIndex:"2"}}><span>!</span></div> :
                                (
                                (referCnt > 0 ? <div className="yellow_c" style={{zIndex:"2"}}><span>!</span></div> : '')
                                )}
                            </div>
                            <video src={`${process.env.PUBLIC_URL}/movie/${imgName}`} className="detailEQMovie" preload='auto' autoPlay loop muted></video>
                        </>
                        : (imgName?.toLowerCase().endsWith('.glb') ?
                        <div 
                            className="esc-model-card"
                            style={{
                                width: '100%',            // 상위 Grid/Flex 컬럼 너비를 100% 채움
                                aspectRatio: '16 / 11',   // 가로:세로 비율 유지 (3D 모델에 맞는 비율 지정)
                                maxHeight: '500px',       // 너무 커지는 것을 방지하는 최대 높이 제한
                                backgroundColor: '#262626',
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}
                        >
                            {/* arrowDirection={'left,right,front,up,back,down'}  
                            info.latestEquipmentOp.opStatus
                            info.latestEquipmentOp.opStatusDetail
                            */}
                            <ModelViewerCard 
                                showAlert={showAlert} alertType={alertType} showExclamation={showAlert} 
                                // showAlert={true} alertType={'alert'} showExclamation={true} 
                                latestEquipmentOp={info?.latestEquipmentOp} /*arrowDirection={'left,right,up,back'}*/
                                // 필요 시 opStatusDetail의 변화를 확실히 알리기 위해 key 지정 가능
                                // key={`${info?.id}_${info?.latestEquipmentOp?.opStatus}_${info?.latestEquipmentOp?.opStatusDetail}`}
                            />
                        </div>
                            :
                        <>
                            <div className="alert_box">
                                {alertCnt > 0 ? <div className="orange_c" style={{zIndex:"2"}}><span>!</span></div> :
                                (
                                (referCnt > 0 ? <div className="yellow_c" style={{zIndex:"2"}}><span>!</span></div> : '')
                                )}
                            </div>
                            <img src={`${process.env.PUBLIC_URL}/img/view/${imgName}`} alt='' />
                        </>
                        )
                    )}
                </div>

                {/* 부품이미지 */}
                <div className='equip_part_img' style={{flexDirection:`${aiIaq ? "column":"row"}`, width:`${aiIaq ? "20%":"40%"}`}}>
                  {equipmentParts && equipmentParts.map((part, pIdx)=>{
                    let imgDisplayMaxCnt = 6;
                    let displayWidth = "50%";
                    if (aiIaq) {
                        imgDisplayMaxCnt = 3;
                        displayWidth = "100%";
                    }

                    const activeVideoPartKey = activeVideoPartId?.split('_').slice(0, 2).join('_');
                    const activeVideoPartFileId = activeVideoPartId?.split('_')[2];

                    // 1. 현재 파트의 고유 키 생성
                    const currentPartKey = `${part.componentLocationCode}_${part.partCode}`;
                    
                    // 2. 현재 파트가 클릭된 상태인지 확인
                    const isVideoActive = activeVideoPartKey === currentPartKey;

                    // 3. 미디어 소스 결정
                    // 클릭된 상태라면 동영상 URL을, 아니면 기본 API URL을 사용
                    const finalFileUrl = isVideoActive 
                        ? `${window.RUNTIME_CONFIG.REACT_APP_API_URL}/api/download/:direct?fileGroup=DCV&id=${activeVideoPartFileId}` // 영상 스트리밍 경로 (예시)
                        : `${window.RUNTIME_CONFIG.REACT_APP_API_URL}/api/download/${part.fileId}`;
                        
                    const finalExtension = isVideoActive ? 'mp4' : (part.fileContentType?.split('/')[1] || 'png');
                    const finalStatus = isVideoActive ? 'active' : (info.latestEquipmentOp?.opStatus || 'stop');
                    // console.log(part)
                    if (pIdx < imgDisplayMaxCnt) {
                      return (
                  <div key={`${currentPartKey}_${pIdx}`} style={{width:`${displayWidth}`, textAlign:"center", cursor:`${(part.componentId && part.partId) ? 'pointer':''}`}}
                        onClick={() => {
                            if (!part.componentId || !part.partId) return;
                            equipmentPartsByComponentId(part.componentId);
                            setLocationCode(part.componentLocationCode)
                        }}
                  >
                    <p>{part.partName ? `${part.componentLocationName} - ${part.partName}` : ''}&nbsp;{/*&nbsp;{part.partPosition}*/}</p>
                    {part.partName && part.fileId > 0 ?
                        <FileViewerByFileUrl 
                            fileUrl={finalFileUrl} 
                            extension={finalExtension} 
                            status={finalStatus} 
                            muted={true}
                            sampleImgSrc={`${process.env.PUBLIC_URL}/img/view/sample1-1.png`}
                        />
                    :
                        <FileViewerByFileUrl 
                            fileUrl={`${process.env.PUBLIC_URL}/img/view/sample1-1.png`} 
                            // fileUrl={`${process.env.PUBLIC_URL}/img/view/${mode === 'light' ? "sample1.png" : "sample1-1.png"}`} 
                            extension={'.png'} 
                            muted={true} 
                        />
                    }
                    
                  </div>
                      )
                    }
                  })}
                </div>
              </div>
            </div>

            <EquipmentDurationGraphs equipment={info} equipmentId={info.id} target={'equipmentView'} />

        </React.Fragment>
    )
}

export default StatusTab;